import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { Button, CircularProgress } from '@mui/material'
import Link from 'gui/components/link'
import { GET_PARENT_COURSE_ID } from './queries'
import type { GraphQLContentReference } from './types'

interface ViewParentCourseButtonProps {
  /** The course module ID (UUID) */
  moduleId: string
}

// Type for contentReferencesFrom query result - contentTo fields match our query
type ParentCourseReference = Omit<GraphQLContentReference, 'contentTo'> & {
  contentTo?: Array<{
    id: string
    contentId?: number
    contentType?: string
    title?: string
  } | null> | null
}

/**
 * Button component that fetches and navigates to the parent course
 * of a course module. Fetches the parent course ID via GraphQL and
 * provides a link to navigate to the course detail page.
 */
function ViewParentCourseButton ({ moduleId }: ViewParentCourseButtonProps) {
  // Fetch parent course ID
  const { loading, error, data } = useQuery(GET_PARENT_COURSE_ID, {
    variables: { id: moduleId },
    skip: !moduleId,
  })

  // Extract parent course from GraphQL response
  const parentCourse = useMemo(() => {
    const content = data?.contentList?.content?.[0]
    const references = content?.contentReferencesFrom || []

    // Find the first reference with type "course-module" that points to a course
    // contentReferencesFrom returns contentTo (the "from" content, which is the course)
    // Note: contentTo can be an array (for multi-language content) or a single object
    const courseReference = references.find((ref: ParentCourseReference) => {
      const contentTo = ref?.contentTo
      if (!contentTo) return false

      // Handle both array and single object cases
      const contentToArray = Array.isArray(contentTo) ? contentTo : [contentTo]
      return contentToArray.some((item) => item?.contentType === 'course')
    })

    if (!courseReference) return null

    const contentTo = courseReference?.contentTo
    if (!contentTo) return null

    // Handle both array and single object cases
    const contentToArray = Array.isArray(contentTo) ? contentTo : [contentTo]
    return contentToArray.find((item) => item?.contentType === 'course') || null
  }, [data])

  // Show loading state
  if (loading) {
    return (
      <Button variant="outlined" disabled startIcon={<CircularProgress size={16} />}>
        Loading...
      </Button>
    )
  }

  // Show error state or if no parent course found
  if (error || !parentCourse) {
    return null
  }

  // Render button with link to parent course
  return (
    <Link href={`/content/${parentCourse.id}`}>
      <Button variant="outlined" color="primary">
        View Parent Course
      </Button>
    </Link>
  )
}

export default ViewParentCourseButton
